<template>
    <div class="ActionReviewDetails">
        <!-- PRODUCT-26110 Task 13 static emit audit: this component itself also
        `$emit`s 'back' directly (see @cancel below), so it now declares 'back' in
        its own `emits` option (Vue 3 warns otherwise). That makes Vue exclude
        `onBack` from this component's own `$attrs` (a declared emit is treated as
        "handled here", not a pure fallthrough attr) -- which used to reach
        NavigationalHeader implicitly via `v-bind="$attrs"` alone. Forward it
        explicitly instead so NavigationalHeader's own back button keeps working. -->
        <!-- Also shown whenever readOnly is true: DocumentDataReview and ContractExtraction
        normally supply their own back control from inside reviewComp, but that branch is
        skipped in favor of ResolutionSummary below, so this header is the only way back
        for those two types once the action is read-only. -->
        <NavigationalHeader
            v-if="shouldShowNavigationHeader || readOnly"
            :title="value"
            :current-position="currentPosition"
            :max-position="maxPosition"
            :title-prefix="actionTypeTitlePrefix"
            :scope-label="isGlobalAction ? 'Global' : ''"
            v-bind="$attrs"
            @back="$emit('back')"
        />
        <!-- A decided action is shown on the screen it was decided on: same field, same value, with
             the recorded decision where the controls were. The summary below it keeps who decided it
             and when, and carries the Revert control. -->
        <div v-if="showReadOnlyReviewScreen" class="ActionReviewDetails__content">
            <component
                :is="reviewComp"
                :key="actionReview.id"
                v-bind="actionDetailsComponentSpecificProps"
                class="ActionReviewDetails__readOnlyReview"
                :action-review="actionReview"
                read-only
            />
            <ResolutionSummary
                class="ActionReviewDetails__readOnlySummary"
                :resolution="actionReview.resolution"
                :action-type="actionType"
            >
                <template #actions>
                    <RevertActionButton
                        :revertable="actionReview.revertable"
                        :not-revertable-reason="actionReview.notRevertableReason"
                        :global-scope="isGlobalAction"
                        @revert="$emit('revert')"
                    />
                </template>
            </ResolutionSummary>
        </div>
        <ResolutionSummary
            v-else-if="readOnly"
            class="ActionReviewDetails__content"
            :resolution="actionReview.resolution"
            :action-type="actionType"
        >
            <template #actions>
                <RevertActionButton
                    :revertable="actionReview.revertable"
                    :not-revertable-reason="actionReview.notRevertableReason"
                    :global-scope="isGlobalAction"
                    @revert="$emit('revert')"
                />
            </template>
        </ResolutionSummary>
        <component
            :is="reviewComp"
            v-else-if="reviewComp && !hasStandingResolution"
            :key="actionReview.id"
            v-bind="actionDetailsComponentSpecificProps"
            class="ActionReviewDetails__content"
            :action-review="actionReview"
            @resolve="resolve"
            @cancel="$emit('back')"
        />
        <ActionableAlert
            v-else-if="hasStandingResolution"
            class="ActionReviewDetails__standingResolutionNotice"
            title="This action's decision still stands and cannot be changed here."
        />
    </div>
</template>

<style lang="scss">
.ActionReviewDetails {
    height: 100%;
    display: flex;
    flex-direction: column;

    &__content {
        flex: 1;
    }

    &__standingResolutionNotice {
        margin: 15px 0;
    }

    .ComplianceWithCategoryResolutionReview__table,
    .MatchingSelectionReviewBase__table,
    .AcceptanceReviewContent__table,
    .EvaluatedEntityMappingReview__table,
    .NonExtractionReview__table,
    .EntityActionsTable__table {
        .Table__container {
            // all actions details currently are page scroll no need to allow table to scroll since table height
            // is based on the content, which can be very small, when it scrolls, the dropdown within will expand
            // the table content and table scrolls, the dropdown is not very visiable.
            overflow: unset;
        }
    }
}
</style>

<script lang="ts">

    import { Component, defineComponent, PropType } from 'vue';

    import {
        ActionResolveInput, ActionReview,
        ActionStatus,
        ActionType,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

    import ContractExtractionReview
        from '@/modules/contract-extraction/components/ContractExtractionReview/ContractExtractionReview.vue';
    import DocumentDataReview from '@/modules/document-data-review/components/commons/document-data-review/DocumentDataReview.vue';
    import AieManuscriptCategorization
        from '@/modules/entity-actions-review/components/AieManuscriptCategorization/AieManuscriptCategorization.vue';
    import ComplianceWithCategoryResolutionReview
        from '@/modules/entity-actions-review/components/ComplianceWithCategoryResolutionReview/ComplianceWithCategoryResolutionReview.vue';
    import EndorsementReview from '@/modules/entity-actions-review/components/EndorsementReview/EndorsementReview.vue';
    import EntityNameMatching
        from '@/modules/entity-actions-review/components/EntityNameMatching/EntityNameMatching.vue';
    import EvaluatedEntityMappingReview
        from '@/modules/entity-actions-review/components/EvaluatedEntityMappingReview/EvaluatedEntityMappingReview.vue';
    import NavigationalHeader
        from '@/modules/entity-actions-review/components/NavigationalHeader/NavigationalHeader.vue';
    import NonExtractionReview
        from '@/modules/entity-actions-review/components/NonExtractionReview/NonExtractionReview.vue';
    import ResolutionSummary
        from '@/modules/entity-actions-review/components/ResolutionSummary/ResolutionSummary.vue';
    import RevertActionButton
        from '@/modules/entity-actions-review/components/RevertActionButton/RevertActionButton.vue';
    import { EntityActionScopeType } from '@/modules/entity-actions-review/types';
    import { getActionScopeType } from '@/modules/entity-actions-review/utils/getActionScopeType';
    import { getActionValue } from '@/modules/entity-actions-review/utils/getActionValue';
    import ActionableAlert from '@/modules/entity-details/components/ActionableAlert/ActionableAlert.vue';

    /* Action types whose review screen can render itself read-only, showing the recorded decision
       where its controls were. A type is listed only once its component honours `read-only`;
       anything absent falls back to the resolution summary, because rendering a live review screen
       for a decided action would offer controls that must not be there.

       documentDataReview and contractExtraction are deliberately absent: they are document viewers
       that load on mount and supply their own back control, so replaying them read-only is a
       different job from showing a field, a value and a decision. */
    const READ_ONLY_CAPABLE_ACTION_TYPES: ActionType[] = [
        ActionType.entityNameMatchingV1,
        ActionType.aieManuscriptCategorization,
        ActionType.endorsementFormNumberV1,
        ActionType.complianceWithCategoryResolution,
        ActionType.evaluatedEntityMappingV1,
        ActionType.nonExtractionCriterionResolutionV1,
    ];

    const COMPONENT_MAP = {
        [ActionType.complianceWithCategoryResolution]: ComplianceWithCategoryResolutionReview,
        [ActionType.contractExtraction]: ContractExtractionReview,
        [ActionType.endorsementFormNumberV1]: EndorsementReview,
        [ActionType.entityNameMatchingV1]: EntityNameMatching,
        [ActionType.evaluatedEntityMappingV1]: EvaluatedEntityMappingReview,
        [ActionType.nonExtractionCriterionResolutionV1]: NonExtractionReview,
        [ActionType.aieManuscriptCategorization]: AieManuscriptCategorization,
        [ActionType.documentDataReview]: DocumentDataReview,
    };

    export default defineComponent({
        name: 'ActionReviewDetails',
        components: {
            ActionableAlert,
            ComplianceWithCategoryResolutionReview,
            ContractExtractionReview,
            EndorsementReview,
            EntityNameMatching,
            EvaluatedEntityMappingReview,
            NavigationalHeader,
            NonExtractionReview,
            DocumentDataReview,
            ResolutionSummary,
            RevertActionButton,
        },
        props: {
            actionReview: {
                type: Object as PropType<ActionReview>,
                required: true,
            },
            currentPosition: {
                type: Number,
                default: 0,
            },
            maxPosition: {
                type: Number,
                default: 0,
            },
            endorsementCategories: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
            /* Set by the tab the detail view was opened from (the Completed tab), not by the
               action's own status. It decides whether the interactive resolve component is
               reachable at all -- see hasStandingResolution below for the separate, status-based
               guard this does not replace. */
            readOnly: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'back', 'resolve', 'revert' ],
        data() {
            return {
                actionTypeSpecificProps: {
                    [ActionType.endorsementFormNumberV1]: {
                        'endorsement-categories': (this as any).endorsementCategories,
                    },
                } as Partial<Record<ActionType, any>>,
            };
        },
        computed: {
            actionType(): ActionType {
                return (this as any).actionReview.action.$action;
            },
            isGlobalAction(): boolean {
                return getActionScopeType((this as any).actionReview.action) === EntityActionScopeType.global;
            },
            shouldShowNavigationHeader(): boolean {
                return ![
                    ActionType.documentDataReview,
                    ActionType.contractExtraction,
                ].includes((this as any).actionType);
            },
            actionTypeTitlePrefix(): string {
                // [entityMappingExpansion]: only collateral entity atm, get different text per type when expanded
                return (this as any).actionReview.action.$action === ActionType.evaluatedEntityMappingV1
                    ? 'Collateral'
                    : '';
            },
            value(): any {
                return getActionValue((this as any).actionReview);
            },
            reviewComp(): Component | null {
                return COMPONENT_MAP[this.actionReview.action.$action];
            },
            showReadOnlyReviewScreen(): boolean {
                return (this as any).readOnly
                    && READ_ONLY_CAPABLE_ACTION_TYPES.includes((this as any).actionType)
                    && Boolean((this as any).reviewComp);
            },
            actionDetailsComponentSpecificProps() {
                return (this as any).actionTypeSpecificProps[(this as any).actionType] || {};
            },
            /* Kept as a second, independent guard alongside readOnly rather than folded into it.
               readOnly reflects the tab the detail view was opened from; this reflects the
               action's own status. They agree in the normal case, but a Pending list can go
               stale -- an action resolved in another session still sits in this session's
               Pending queue with readOnly false. This is the check that still catches that case
               and falls back to the notice instead of exposing live resolve controls on an
               action that already has a standing decision.

               Checked against completed specifically, not against "not pending", because a
               reverted action also carries a stale status right up until it is decided again:
               it keeps its resolution row but has its completedAt cleared, and it is actionable
               again -- it reappears in the Pending queue precisely so it can be decided
               differently. Its controls must stay live. */
            hasStandingResolution(): boolean {
                return (this as any).actionReview.status === ActionStatus.completed;
            },
        },
        methods: {
            resolve(data: ActionResolveInput): void {
                (this as any).$emit('resolve', data);
            },
        },
    });
</script>
